<template>
    <FormElement
        class="EnumFilter"
        :label="label"
    >
        <FormInput>
            <Dropdown
                :multi-select="isArrayEnum(schema)"
                :options="enumOptions"
                :selected="selectedEnumOption"
                @input="onInput"
                @open="$emit('open')"
            />
        </FormInput>
    </FormElement>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { Dropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import JsonSchema, { JsonSchemaArray, JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { isArray, isEnum } from '@evidentid/json-schema/schemaChecks';

    export default defineComponent({
        name: 'EnumFilter',
        components: {
            FormElement, FormInput,
            Dropdown,
        },
        props: {
            value: {
                type: [ String, Array ] as PropType<string | string[]>,
                default: undefined,
            },
            label: {
                type: String as PropType<string>,
                default: '',
            },
            schema: {
                type: Object as PropType<JsonSchemaEnum | JsonSchemaArray<JsonSchemaEnum>>,
                default: undefined,
            },
        },
        emits: [ 'input', 'open' ],
        computed: {
            enumItems(): string[] {
                const { schema } = this;
                if (!schema) {
                    return [];
                }
                return this.isArrayEnum(schema) ? schema.items.enum : schema.enum;
            },
            enumLabels(): string[] | null {
                const { schema } = this;
                if (!schema) {
                    return null;
                }
                return this.isArrayEnum(schema)
                    ? schema.items.enumLabels || null
                    : schema.enumLabels || null;
            },
            enumOptions(): DropdownOption[] {
                return this.enumItems.map((item, index) => ({
                    label: this.enumLabels?.[index] || item,
                    value: item,
                }));
            },
            selectedEnumOption(): DropdownOption[] {
                if (Array.isArray(this.value)) {
                    return this.value.map(this.enumValToOption);
                } else if (typeof this.value === 'string' && this.value) {
                    return [ this.enumValToOption(this.value) ];
                } else {
                    return [];
                }
            },
        },
        methods: {
            enumValToOption(val: string): DropdownOption {
                const labelIndex = this.enumItems.indexOf(val);
                const label = this.enumLabels?.[labelIndex] || val;
                return { label, value: val };
            },
            isEnumType(schema: JsonSchema): schema is JsonSchemaEnum {
                return schema && isEnum(schema);
            },
            isArrayEnum(schema: JsonSchema): schema is JsonSchemaArray<JsonSchemaEnum> {
                return schema && isArray(schema) && isEnum(schema.items as JsonSchema);
            },
            onInput(inputVal: DropdownOption | DropdownOption[]): void {
                // single-select Dropdown emits one option, multi-select emits an array
                const options = Array.isArray(inputVal) ? inputVal : [ inputVal ];
                this.$emit('input', options.map((x) => x.value).join(','));
            },
        },
    });
</script>
